import type { CSSProperties } from 'react';
import type { Breakpoint, Responsive } from './responsive';

// Arbitrary-value responsive dimensions. Pairs with the `.rc-responsive-*`
// classes in styles/classes-components/rc-responsive-dimensions.scss (this
// helper is their sole author — they are not a hand-written utility family).
//
// Unlike the enumerated width / height utilities (which take scale values and
// emit a prefixed class per breakpoint), this is the escape hatch for
// hyper-specific, off-scale dimensions: the value is any CSS length string and
// rides in on an inline CSS custom property. Prefer token / enumerated utilities
// when a scale value fits, and the `@include responsive()` SCSS mixin when the
// component already has its own stylesheet; reach here for off-scale values in a
// component that would otherwise need no custom CSS.
//
// SSR-correct by construction: inline `style` + static CSS resolve at first
// paint, so there is no `useMediaQuery`-style null-until-mount layout shift.

/**
 * Dimensional-length properties with a `.rc-responsive-*` class. Keys are
 * camelCase (React style); the SCSS class / CSS-var segment is the kebab-case CSS
 * property (see `DIMENSION_SEGMENTS`). Deliberately narrow — length dimensions
 * only. Extend in lockstep with the `$dimensions` list in the SCSS.
 */
export type ResponsiveDimension =
  | 'width'
  | 'height'
  | 'minWidth'
  | 'maxWidth'
  | 'minHeight'
  | 'maxHeight';

// camelCase prop key → kebab-case CSS property (== the SCSS class / var segment).
const DIMENSION_SEGMENTS: Record<ResponsiveDimension, string> = {
  width: 'width',
  height: 'height',
  minWidth: 'min-width',
  maxWidth: 'max-width',
  minHeight: 'min-height',
  maxHeight: 'max-height',
};

// Per-breakpoint CSS-var suffix; mirrors the SCSS (`-d` / `-t` / `-m`).
const BREAKPOINT_SUFFIXES: Record<Breakpoint, string> = {
  desktop: 'd',
  tablet: 't',
  mobile: 'm',
};

const BREAKPOINTS = Object.keys(BREAKPOINT_SUFFIXES) as Breakpoint[];

/**
 * Builds `{ className, style }` for one or more responsive arbitrary-value
 * dimensions. Each `value` is either a single CSS length (applied at the
 * base / desktop tier) or a partial per-breakpoint object of CSS lengths.
 *
 *   responsiveDimensions({ height: { desktop: '340px', mobile: '180px' } })
 *   // → { className: 'rc-responsive-height',
 *   //     style: { '--responsive-height-d': '340px', '--responsive-height-m': '180px' } }
 *
 * Spread the result onto any element that takes `className` / `style`:
 *
 *   <div {...responsiveDimensions({ width: '340px' })} />
 */
export function responsiveDimensions(
  dimensions: Partial<Record<ResponsiveDimension, Responsive<string>>>,
): { className: string; style: CSSProperties } {
  const classNames: string[] = [];
  const style: Record<string, string> = {};

  (Object.keys(dimensions) as ResponsiveDimension[]).forEach((dimension) => {
    const value = dimensions[dimension];
    if (value === undefined) return;

    const segment = DIMENSION_SEGMENTS[dimension];
    classNames.push(`rc-responsive-${segment}`);

    // A bare string applies at the base / desktop tier; an object is per-breakpoint.
    const perBreakpoint = typeof value === 'string' ? { desktop: value } : value;

    BREAKPOINTS.forEach((breakpoint) => {
      const breakpointValue = perBreakpoint[breakpoint];
      if (breakpointValue !== undefined) {
        style[`--responsive-${segment}-${BREAKPOINT_SUFFIXES[breakpoint]}`] = breakpointValue;
      }
    });
  });

  return { className: classNames.join(' '), style: style as CSSProperties };
}
